/**
 * 스탬프 손잡이(handle) + 손잡이 홀(hole) 처리.
 *
 * - hole.stl: 스탬프 베이스에 ExtrudeGeometry로 hole을 박아 재생성 (CSG 미사용 → winding 깨끗)
 * - hole.stl: 별도 plug 메쉬로 pocket 자리에 삽입
 * - handle.stl: 스탬프 옆에 별도 메쉬로 배치
 *
 * STL은 기본 스케일 기준이고, plainGroup의 부모 transform이 전체 스케일을 담당.
 */

import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";

const HOLE_URL = "/models/cookie_handle_base_V2.STL";
const HANDLE_URL = "/models/cookie_handle_pocket_V2.STL";

const HANDLE_GAP_MM = 20;

let holeGeoCache = null;
let handleGeoCache = null;

function loadGeo(url) {
  return new Promise((resolve, reject) => {
    new STLLoader().load(url, (g) => resolve(g), undefined, reject);
  });
}

export async function preloadHandleHoleGeometries() {
  if (!holeGeoCache) holeGeoCache = await loadGeo(HOLE_URL);
  if (!handleGeoCache) handleGeoCache = await loadGeo(HANDLE_URL);

  const oriented = orientShortestUp(holeGeoCache);
  const plugHeight = oriented.boundingBox.max.y - oriented.boundingBox.min.y;

  return { hole: holeGeoCache, handle: handleGeoCache, plugHeight };
}

/** 가장 긴 축이 Y(수직)이 되도록 회전 */
function orientLongestUp(geometry) {
  const g = geometry.clone();
  g.computeBoundingBox();
  const size = new THREE.Vector3();
  g.boundingBox.getSize(size);

  const axes = [
    { axis: "x", len: size.x },
    { axis: "y", len: size.y },
    { axis: "z", len: size.z },
  ];
  axes.sort((a, b) => b.len - a.len);
  const longest = axes[0].axis;

  if (longest === "x") g.rotateZ(-Math.PI / 2);
  else if (longest === "z") g.rotateX(-Math.PI / 2);
  g.computeBoundingBox();
  return g;
}

/** 가장 짧은 축이 Y(수직)이 되도록 회전 */
function orientShortestUp(geometry) {
  const g = geometry.clone();
  g.computeBoundingBox();
  const size = new THREE.Vector3();
  g.boundingBox.getSize(size);

  const axes = [
    { axis: "x", len: size.x },
    { axis: "y", len: size.y },
    { axis: "z", len: size.z },
  ];
  axes.sort((a, b) => a.len - b.len);
  const shortest = axes[0].axis;

  if (shortest === "x") g.rotateZ(Math.PI / 2);
  else if (shortest === "z") g.rotateX(-Math.PI / 2);
  g.computeBoundingBox();
  return g;
}

/** Z축 180° 뒤집기 */
function flipUpsideDown(geometry) {
  const g = geometry.clone();
  g.rotateZ(Math.PI);
  g.computeBoundingBox();
  return g;
}

/**
 * Three.js Shape의 polygon centroid (무게중심) — bbox 중앙보다 자연스러움
 */
function getShapeCentroid(shape) {
  const pts = shape.getPoints(128);
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    const cross = p1.x * p2.y - p2.x * p1.y;
    area += cross;
    cx += (p1.x + p2.x) * cross;
    cy += (p1.y + p2.y) * cross;
  }
  area *= 0.5;
  if (Math.abs(area) < 1e-9) {
    // fallback: bbox 중앙
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of pts) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
  }
  cx /= 6 * area;
  cy /= 6 * area;
  return { cx, cy };
}

/** Point-in-polygon (ray casting) */
function pointInPolygon(px, py, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y;
    const xj = pts[j].x, yj = pts[j].y;
    const intersect =
      (yi > py) !== (yj > py) &&
      px < ((xj - xi) * (py - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** 점에서 폴리곤 가장 가까운 에지까지 거리 */
function distanceToPolygon(px, py, pts) {
  let minD = Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const ax = pts[j].x, ay = pts[j].y;
    const bx = pts[i].x, by = pts[i].y;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 1e-12 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * dx, qy = ay + t * dy;
    const d = Math.hypot(px - qx, py - qy);
    if (d < minD) minD = d;
  }
  return minD;
}

/**
 * 폴리곤 내부에서 holeRadius + margin이 여유롭게 들어갈 중심점 찾기.
 * 1) centroid가 내부이고 여유가 충분하면 사용
 * 2) 아니면 bbox 격자 샘플링으로 가장 안쪽(가장자리까지 거리 최대) 지점 반환
 */
function findSafeHoleCenter(shape, holeRadius, margin = 0.5) {
  const pts = shape.getPoints(128);
  const required = holeRadius + margin;

  const c = getShapeCentroid(shape);
  if (
    pointInPolygon(c.cx, c.cy, pts) &&
    distanceToPolygon(c.cx, c.cy, pts) >= required
  ) {
    return { cx: c.cx, cy: c.cy };
  }

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const GRID = 40;
  const stepX = (maxX - minX) / GRID;
  const stepY = (maxY - minY) / GRID;
  let best = null;
  let bestD = -Infinity;
  for (let i = 1; i < GRID; i++) {
    for (let j = 1; j < GRID; j++) {
      const x = minX + i * stepX;
      const y = minY + j * stepY;
      if (!pointInPolygon(x, y, pts)) continue;
      const d = distanceToPolygon(x, y, pts);
      if (d > bestD) {
        bestD = d;
        best = { cx: x, cy: y };
      }
    }
  }
  if (best) return best;

  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

/**
 * 원형 hole Path를 만들어 baseShape에 추가.
 * @param {THREE.Shape} baseShape
 * @param {number} cx baseShape XY 평면 hole 중심 X
 * @param {number} cy baseShape XY 평면 hole 중심 Y
 * @param {number} radius hole 반지름
 * @param {number} segments
 */
function addCircleHoleToShape(baseShape, cx, cy, radius) {
  const holePath = new THREE.Path();
  // 시계방향 (Three.js Shape에서 hole은 반대 winding)
  holePath.absellipse(cx, cy, radius, radius, 0, Math.PI * 2, true, 0);
  baseShape.holes = baseShape.holes || [];
  baseShape.holes.push(holePath);
}

/**
 * ExtrudeGeometry(bevel 없음)의 position을 캡 선택적으로 빼고 복사한다.
 * group[0] = 뚜껑(아래 캡 → 위 캡 순), group[1] = 옆면.
 * 슬라이서 "객체 분리"는 정점/에지 공유 단위로 쪼개므로, 맞닿는 파트끼리
 * 링을 공유시키려면 겹치는 캡을 빼고 테두리 정점을 그대로 물려줘야 한다.
 */
function extrudePositions(geo, { dropBottomCap = false, dropTopCap = false } = {}) {
  const src = geo.getAttribute("position").array;
  const lid = geo.groups.find((g) => g.materialIndex === 0);
  const side = geo.groups.find((g) => g.materialIndex === 1);
  if (!lid || !side || lid.count % 2 !== 0 || (!dropBottomCap && !dropTopCap)) {
    return Float32Array.from(src);
  }

  const half = lid.count / 2;
  const ranges = [];
  if (!dropBottomCap) ranges.push([lid.start, half]);
  if (!dropTopCap) ranges.push([lid.start + half, half]);
  ranges.push([side.start, side.count]);

  const total = ranges.reduce((s, r) => s + r[1], 0) * 3;
  const out = new Float32Array(total);
  let off = 0;
  for (const [start, count] of ranges) {
    out.set(src.subarray(start * 3, (start + count) * 3), off);
    off += count * 3;
  }
  return out;
}

function shiftZ(arr, dz) {
  for (let i = 2; i < arr.length; i += 3) arr[i] += dz;
  return arr;
}

/** 삼각형 감김 방향 뒤집기 (캡을 아래로 향하게 할 때) */
function flipWinding(arr) {
  for (let i = 0; i < arr.length; i += 9) {
    for (let k = 0; k < 3; k++) {
      const t = arr[i + 3 + k];
      arr[i + 3 + k] = arr[i + 6 + k];
      arr[i + 6 + k] = t;
    }
  }
  return arr;
}

/* 1µm. Clipper 오프셋 결과에는 1e-5~1e-4mm 떨어진 "거의 같은 점"이 섞여 나온다.
   이걸 남겨두면 캡과 옆면이 서로 다른 복사본을 참조해 T-junction이 생기고,
   정점을 좌표 그대로 대조하는 슬라이서가 열린 에지(non-manifold)로 잡는다.
   프린터 해상도(0.1mm)의 1/100이라 이 정도 병합은 형상에 영향이 없다. */
const PT_EPS = 1e-3;

/**
 * 윤곽 점열에서 끝점 중복/인접 중복점 제거.
 * 남겨두면 ExtrudeGeometry가 길이 0 옆면을 만들어 퇴화 삼각형이 생기고,
 * 슬라이서가 그걸 지우면서 "non-manifold edge" 오류로 잡는다.
 */
function cleanPoints(pts) {
  const out = [];
  for (const p of pts) {
    if (!out.length || p.distanceTo(out[out.length - 1]) > PT_EPS) out.push(p);
  }
  while (out.length > 3 && out[out.length - 1].distanceTo(out[0]) <= PT_EPS) out.pop();
  return out;
}

/** Shape(+holes)을 중복점 없는 폴리곤 Shape로 다시 만든다 */
function cleanShape(shape, cs) {
  const s = new THREE.Shape(cleanPoints(shape.getPoints(cs)));
  s.holes = (shape.holes || []).map((h) => new THREE.Path(cleanPoints(h.getPoints(cs))));
  return s;
}

/** 폴리곤 부호 있는 면적 (안쪽/바깥쪽 판정용) */
function polygonArea(pts) {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += pts[j].x * pts[i].y - pts[i].x * pts[j].y;
  }
  return a / 2;
}

/** pts(폴리곤)가 poly 안에 들어있는지 — 꼭짓점 다수결이라 경계 접촉에도 흔들리지 않는다 */
function polygonInside(pts, poly) {
  const n = Math.min(pts.length, 9);
  let inside = 0;
  for (let i = 0; i < n; i++) {
    const p = pts[Math.floor((i * pts.length) / n)];
    if (pointInPolygon(p.x, p.y, poly)) inside++;
  }
  return inside * 2 > n;
}

function ringShape(outerPts, innerPts) {
  const s = new THREE.Shape(outerPts);
  s.holes.push(new THREE.Path(innerPts));
  return s;
}

/**
 * 소켓 바깥 바닥 링을 스탬프 베이스 Shape 좌표로 뽑는다.
 * (메쉬 공간 (x, 높이, z) → Shape 공간 (x, -z) — rotateX(-90°)의 역변환)
 * 포켓을 "소켓이 실제로 쓰는 그 점들"로 뚫어야 둘이 정점을 공유해 한 덩어리가 된다.
 * @returns {Array<{x:number,y:number}>|null}
 */
function outerBottomRing(geo) {
  const pos = geo.getAttribute("position");
  geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const cx = (bb.min.x + bb.max.x) / 2;
  const cz = (bb.min.z + bb.max.z) / 2;

  let maxR = 0;
  for (let i = 0; i < pos.count; i++) {
    const r = Math.hypot(pos.getX(i) - cx, pos.getZ(i) - cz);
    if (r > maxR) maxR = r;
  }

  const seen = new Set();
  const pts = [];
  for (let i = 0; i < pos.count; i++) {
    if (Math.abs(pos.getY(i) - bb.min.y) > 1e-4) continue;
    const x = pos.getX(i);
    const z = pos.getZ(i);
    if (maxR - Math.hypot(x - cx, z - cz) > 0.01) continue;
    const k = `${x},${z}`;
    if (seen.has(k)) continue;
    seen.add(k);
    pts.push({ x, z, a: Math.atan2(z - cz, x - cx) });
  }
  if (pts.length < 3) return null;
  pts.sort((p, q) => p.a - q.a);
  return pts.map((p) => ({ x: p.x, y: -p.z }));
}

/**
 * 소켓에서 "스탬프 재료 속에 파묻히는 면"(바깥 원통 + 윗면)을 걷어낸다.
 * 포켓 벽/천장을 소켓 바깥면이 대신하므로, 양쪽을 다 남기면 같은 면이 두 겹이 되어
 * 비매니폴드 에지가 생긴다. 한쪽을 걷어내면 둘이 이어진 하나의 솔리드가 된다.
 * @returns {THREE.BufferGeometry|null} 걷어낼 면을 못 찾으면 null (호출측에서 폴백)
 */
export function stripSocketOuterShell(geo) {
  const src = geo.index ? geo.toNonIndexed() : geo;
  const pos = src.getAttribute("position");

  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxY = -Infinity;
  for (let i = 0; i < pos.count; i++) {
    minX = Math.min(minX, pos.getX(i));
    maxX = Math.max(maxX, pos.getX(i));
    minZ = Math.min(minZ, pos.getZ(i));
    maxZ = Math.max(maxZ, pos.getZ(i));
    maxY = Math.max(maxY, pos.getY(i));
  }
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  let maxR = 0;
  for (let i = 0; i < pos.count; i++) {
    maxR = Math.max(maxR, Math.hypot(pos.getX(i) - cx, pos.getZ(i) - cz));
  }

  const keep = [];
  let dropped = 0;
  for (let t = 0; t < pos.count / 3; t++) {
    let onTop = true;
    let onWall = true;
    for (let k = 0; k < 3; k++) {
      const i = t * 3 + k;
      if (Math.abs(pos.getY(i) - maxY) > 1e-3) onTop = false;
      if (maxR - Math.hypot(pos.getX(i) - cx, pos.getZ(i) - cz) > 1e-2) onWall = false;
    }
    if (onTop || onWall) {
      dropped++;
      continue;
    }
    for (let k = 0; k < 3; k++) {
      const i = t * 3 + k;
      keep.push(pos.getX(i), pos.getY(i), pos.getZ(i));
    }
  }
  if (src !== geo) src.dispose();
  if (!dropped || !keep.length) return null;

  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.Float32BufferAttribute(keep, 3));
  return out;
}

function circlePoints(cx, cy, r, segments) {
  const pts = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    pts.push(new THREE.Vector2(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
  }
  return pts;
}

function extrude(shape, depth, cs) {
  return new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: false,
    curveSegments: cs,
    steps: 1,
  });
}

/** 위(+Z)를 보는 평면 캡 */
function capPositions(shapes, cs) {
  const g = new THREE.ShapeGeometry(shapes, cs).toNonIndexed();
  const arr = Float32Array.from(g.getAttribute("position").array);
  g.dispose();
  return arr;
}

function mergePositions(chunks) {
  const total = chunks.reduce((s, c) => s + c.length, 0);
  if (!total) return null;
  const merged = new Float32Array(total);
  let off = 0;
  for (const c of chunks) {
    merged.set(c, off);
    off += c.length;
  }
  if (!merged.every(Number.isFinite)) return null;

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(merged, 3));
  geo.rotateX(-Math.PI / 2);
  geo.computeVertexNormals();
  return geo;
}

/**
 * 커터(벽 + 플랜지)를 이음매 없는 단일 솔리드로 만든다.
 * 벽과 플랜지를 각각 워터타이트로 만들어 겹치면 맞닿는 링에 면이 4개 모여
 * non-manifold가 되므로, 겹치는 캡을 빼고 층을 이어 붙인다.
 *
 *   바닥캡 ring(base,inner) → 바깥벽 base(0~baseH) → 플랜지 윗면 ring(base,wall)
 *   → 벽 바깥면 wall(baseH~knifeH) → 윗면 ring(wall,inner) → 안쪽면 inner(0~knifeH)
 */
export function buildWeldedCutterGeometry({
  innerShape,
  wallShape,
  baseShape,
  knifeH,
  baseH,
  curveSegments,
}) {
  if (!innerShape || !wallShape || !(knifeH > 0)) return null;
  const cs = curveSegments || 60;

  try {
    const inner = cleanPoints(innerShape.getPoints(cs));
    const wall = cleanPoints(wallShape.getPoints(cs));
    const base = baseShape ? cleanPoints(baseShape.getPoints(cs)) : null;

    // 플랜지가 없거나 벽보다 높으면 단일 링 압출로 충분
    if (!base || !(baseH > 0) || baseH >= knifeH) {
      return mergePositions([extrudePositions(extrude(ringShape(wall, inner), knifeH, cs))]);
    }

    const chunks = [];
    // 플랜지 층: 위 캡만 빼고 (바닥캡 + 바깥벽 + 안쪽벽)
    chunks.push(extrudePositions(extrude(ringShape(base, inner), baseH, cs), { dropTopCap: true }));
    // 플랜지 윗면 (벽 바깥선까지)
    chunks.push(shiftZ(capPositions([ringShape(base, wall)], cs), baseH));
    // 칼날 층: 아래 캡만 빼고
    chunks.push(
      shiftZ(
        extrudePositions(extrude(ringShape(wall, inner), knifeH - baseH, cs), {
          dropBottomCap: true,
        }),
        baseH
      )
    );
    return mergePositions(chunks);
  } catch (e) {
    console.warn("[handleHole] 커터 용접 지오메트리 생성 실패:", e);
    return null;
  }
}

/**
 * 스탬프(베이스 + 도장면)를 한 덩어리로 이어진 지오메트리로 만든다.
 * - 베이스 윗면 캡에 도장면 윤곽만큼 구멍을 뚫고
 * - 도장면은 바닥 캡을 빼서 그 구멍 테두리와 정점을 공유시킨다
 * 부피/외형은 기존과 동일하고 내부 토폴로지만 이어붙는다.
 *
 * @returns {THREE.BufferGeometry|null} 실패 시 null (호출측에서 기존 방식으로 폴백)
 */
export function buildWeldedStampGeometry({
  baseShape,
  baseDepth,
  curveSegments,
  pocket,
  socketWall,
  faceShapes,
  faceDepth,
}) {
  if (!baseShape || !(baseDepth > 0)) return null;

  const cs = curveSegments || 60;
  const base = cleanShape(baseShape, cs);
  const basePts = base.getPoints(cs);
  const faces = (faceShapes || []).filter(Boolean).map((f) => cleanShape(f, cs));
  const chunks = [];

  try {
    /* 1) 포켓 층 (0 ~ pocketDepth): 원형 hole. 윗면 캡은 본체와 겹치므로 뺀다 */
    const topStart = pocket && pocket.depth > 0 && pocket.r > 0 ? pocket.depth : 0;
    if (topStart > 0) {
      // 소켓이 쓰는 링이 있으면 그걸로 뚫는다 → 소켓과 정점을 공유해 한 덩어리가 된다
      const holePts =
        pocket.ringPts && pocket.ringPts.length >= 3
          ? pocket.ringPts.map((p) => new THREE.Vector2(p.x, p.y))
          : circlePoints(pocket.cx, pocket.cy, pocket.r, Math.max(48, cs));

      if (socketWall) {
        // 포켓 벽·천장은 소켓 바깥면이 대신한다 → 바닥 캡과 베이스 바깥벽만 만든다
        chunks.push(flipWinding(capPositions([ringShape(basePts, holePts)], cs)));
        chunks.push(
          extrudePositions(extrude(new THREE.Shape(basePts), topStart, cs), {
            dropBottomCap: true,
            dropTopCap: true,
          })
        );
      } else {
        const pocketShape = ringShape(basePts, holePts);
        chunks.push(extrudePositions(extrude(pocketShape, topStart, cs), { dropTopCap: true }));
        // 포켓 천장 (아래를 보는 원판) — 포켓 안쪽만 막는다
        chunks.push(
          shiftZ(flipWinding(capPositions([new THREE.Shape(holePts)], cs)), topStart)
        );
      }
    }

    /* 2) 본체 층 (topStart ~ baseDepth): 아래/위 캡 모두 따로 처리 */
    const bodyDepth = baseDepth - topStart;
    if (bodyDepth <= 0) return null;
    chunks.push(
      shiftZ(
        extrudePositions(extrude(new THREE.Shape(basePts), bodyDepth, cs), {
          dropBottomCap: topStart > 0,
          dropTopCap: true,
        }),
        topStart
      )
    );

    /* 3) 윗면 캡: 도장면 윤곽을 hole로, 도장면 내부 hole은 메움 패치로.
       주의: 다른 도장면의 hole 안에 들어앉은 도장면(구멍 속 섬)은 베이스 캡이 아니라
       "그 hole을 메우는 패치"의 hole이어야 한다. 구멍 안에 구멍을 넣으면
       삼각분할(earcut) 입력이 깨져 캡에 빈 자리가 생기고,
       슬라이서가 그걸 열린 면(non-manifold edge)으로 잡는다. */
    const capShape = new THREE.Shape(basePts);
    const capShapes = [capShape];

    // 3-1) 도장면 내부 hole을 메우는 패치를 먼저 만든다
    const patches = [];
    for (const f of faces) {
      for (const h of f.holes || []) {
        const pts = h.getPoints(cs);
        if (pts.length < 3) continue;
        const shape = new THREE.Shape(pts);
        patches.push({ pts, shape, area: Math.abs(polygonArea(pts)) });
        capShapes.push(shape);
      }
    }

    // 3-2) 도장면 윤곽은 자신을 감싸는 가장 안쪽 패치의 hole로, 없으면 베이스 캡의 hole로
    for (const f of faces) {
      const pts = f.getPoints(cs);
      if (pts.length < 3) continue;
      let owner = null;
      for (const p of patches) {
        if (!polygonInside(pts, p.pts)) continue;
        if (!owner || p.area < owner.area) owner = p;
      }
      (owner ? owner.shape : capShape).holes.push(new THREE.Path(pts));
    }

    chunks.push(shiftZ(capPositions(capShapes, cs), baseDepth));

    /* 4) 도장면: 바닥 캡 제거 → 윗면 캡 구멍 테두리와 정점 공유 */
    if (faceDepth > 0) {
      for (const f of faces) {
        chunks.push(
          shiftZ(extrudePositions(extrude(f, faceDepth, cs), { dropBottomCap: true }), baseDepth)
        );
      }
    }
  } catch (e) {
    console.warn("[handleHole] 스탬프 용접 지오메트리 생성 실패:", e);
    return null;
  }

  return mergePositions(chunks);
}

/**
 * 스탬프 그룹에 hole(홀)과 handle(손잡이)을 추가한다.
 */
export function addHandleAndHole(stampGroup, opts = {}) {
  if (!stampGroup) return;
  if (!holeGeoCache || !handleGeoCache) return;

  /* 손잡이 계열(손잡이·소켓 구멍·plug) 공통 배율 (2026-09-10 김현비 — OS 와 손잡이 크기 통일).
     셋은 물리적으로 맞물리므로 **반드시 같은 배율**이어야 한다 — 손잡이만 줄이면 구멍이
     헐거워진다. 세 개 다 아래 두 원본(holeGeoCache·handleGeoCache)에서 나오므로
     여기서 한 번만 걸면 된다.
     기본 1 = 종전 그대로. 호출부(Cutter.js)가 커터 크기에 맞춘 값을 넘긴다. */
  const partScale = Number(opts.scale) > 0 ? Number(opts.scale) : 1;

  // 1. stampBase 메쉬 찾기
  let stampBaseMesh = null;
  stampGroup.traverse((obj) => {
    if (obj.isMesh && obj.userData?.partType === "stampBase" && !stampBaseMesh) {
      stampBaseMesh = obj;
    }
  });
  if (!stampBaseMesh) {
    console.warn("[handleHole] stampBase 메쉬 못찾음");
    return;
  }

  const baseShape = stampBaseMesh.userData.baseShape;
  const baseDepth = stampBaseMesh.userData.baseDepth;
  const curveSegments = stampBaseMesh.userData.curveSegments || 60;
  if (!baseShape || !baseDepth) {
    console.warn("[handleHole] baseShape/baseDepth userData 없음");
    return;
  }

  // 2. hole STL 준비 — 반지름과 native pocket 깊이 (Y 축 = 가장 짧은 축)
  const holeGeo = orientShortestUp(holeGeoCache);
  if (partScale !== 1) holeGeo.scale(partScale, partScale, partScale);
  const holeRadius = Math.max(
    (holeGeo.boundingBox.max.x - holeGeo.boundingBox.min.x) / 2,
    (holeGeo.boundingBox.max.z - holeGeo.boundingBox.min.z) / 2
  );
  const nativeHoleDepth = holeGeo.boundingBox.max.y - holeGeo.boundingBox.min.y;
  // pocket 깊이 = STL native 깊이, 최대 baseDepth - 1mm (천장 1mm는 남기기)
  const pocketDepth = Math.min(nativeHoleDepth, Math.max(0.5, baseDepth - 1));

  // 3. hole 위치 = 폴리곤 내부 안전 중심 (centroid 우선, 벗어나면 inscribed-circle 근사)
  //    돌고래처럼 비대칭/곡선 형상에서 bbox 중앙은 도형 밖으로 빠질 수 있어 plug가 튀어나옴.
  const sb = findSafeHoleCenter(baseShape, holeRadius, 0.5);

  // 4. 두 층으로 extrude:
  //    bottom (0 ~ pocketDepth): 원형 hole이 뚫린 ring
  //    top (pocketDepth ~ baseDepth): hole 없는 solid
  const bottomShape = new THREE.Shape(baseShape.getPoints(curveSegments));
  addCircleHoleToShape(bottomShape, sb.cx, sb.cy, holeRadius);

  const topShape = new THREE.Shape(baseShape.getPoints(curveSegments));

  let bottomGeo = new THREE.ExtrudeGeometry(bottomShape, {
    depth: pocketDepth,
    bevelEnabled: false,
    curveSegments,
    steps: 1,
  });

  let topGeo = new THREE.ExtrudeGeometry(topShape, {
    depth: baseDepth - pocketDepth,
    bevelEnabled: false,
    curveSegments,
    steps: 1,
  });
  topGeo.translate(0, 0, pocketDepth); // top을 pocket 위로 올림

  // 두 geometry 병합 (둘 다 +Z 방향으로 extrude되어 있음, 그 다음에 X 회전)
  let newGeo = BufferGeometryUtils.mergeGeometries([bottomGeo, topGeo], false);
  newGeo.rotateX(-Math.PI / 2);
  newGeo.computeVertexNormals();

  // 5. 임시 stamp bbox 계산 (plug 위치용 — base+plug 합치기 전)
  newGeo.computeBoundingBox();
  const stampBox = newGeo.boundingBox.clone();

  const holeBox = holeGeo.boundingBox.clone();
  const holeCenter = new THREE.Vector3();
  holeBox.getCenter(holeCenter);

  // plug 위치 (centroid 기준, pocket과 동일)
  const plugStampX = sb.cx;
  const plugStampZ = -sb.cy;

  // 소켓 바닥은 스탬프 바닥과 정확히 같은 높이 — 튀어나오면 화면에서도 어색하고
  // 인쇄할 때 소켓만 베드에 닿아 스탬프 바닥 첫 레이어가 뜬다.
  const plugGeo = holeGeo.clone();
  plugGeo.translate(
    plugStampX - holeCenter.x,
    stampBox.min.y - holeBox.min.y,
    plugStampZ - holeCenter.z
  );

  // 6. stampBase와 plug를 별도 메쉬로 유지 (치수 라벨이 plug를 제외할 수 있도록)
  const newMesh = new THREE.Mesh(newGeo, stampBaseMesh.material);
  newMesh.userData.partType = "stampBase";
  newMesh.userData.baseShape = baseShape;
  newMesh.userData.baseDepth = baseDepth;
  newMesh.userData.curveSegments = curveSegments;
  // STL 내보내기에서 베이스를 다시 만들 때 필요 (포켓 위치/크기)
  newMesh.userData.pocket = {
    cx: sb.cx,
    cy: sb.cy,
    r: holeRadius,
    depth: pocketDepth,
    // 소켓과 포켓이 같은 점을 쓰게 해서 STL에서 한 덩어리로 이어붙인다.
    // 소켓이 포켓보다 높으면(얇은 베이스) 벽을 맡길 수 없으므로 깊이가 맞을 때만.
    ringPts:
      Math.abs(pocketDepth - nativeHoleDepth) < 1e-6 ? outerBottomRing(plugGeo) : null,
  };

  const plugMesh = new THREE.Mesh(plugGeo, stampBaseMesh.material);
  plugMesh.userData.partType = "stampBasePlug";

  const parent = stampBaseMesh.parent;
  parent.remove(stampBaseMesh);
  stampBaseMesh.geometry?.dispose?.();
  parent.add(newMesh);
  parent.add(plugMesh);

  // handle 배치는 기존처럼 base+plug 합쳐진 전체 bbox 기준 (시각적 위치 유지)
  newMesh.geometry.computeBoundingBox();
  plugGeo.computeBoundingBox();
  const stampBoxFinal = newMesh.geometry.boundingBox.clone();
  stampBoxFinal.union(plugGeo.boundingBox);
  const stampCenter = new THREE.Vector3();
  stampBoxFinal.getCenter(stampCenter);

  // 7. handle 메쉬 (긴 축 수직 + 위아래 뒤집기)
  let handleGeo = orientLongestUp(handleGeoCache);
  handleGeo = flipUpsideDown(handleGeo);
  if (partScale !== 1) {
    handleGeo.scale(partScale, partScale, partScale);
    handleGeo.computeBoundingBox();
  }
  const handleBox = handleGeo.boundingBox.clone();
  const handleCenter = new THREE.Vector3();
  handleBox.getCenter(handleCenter);

  const handleMat = new THREE.MeshPhongMaterial({
    color: 0xC9A567,
    shininess: 25,
  });
  const handleMesh = new THREE.Mesh(handleGeo, handleMat);
  handleMesh.userData.partType = "stampHandle";
  // fitRootByShapeBounds 후 균등 스케일 보정에 필요한 native 값 저장
  handleMesh.userData.nativeMinY = handleBox.min.y;
  handleMesh.userData.stampNativeMinY = stampBoxFinal.min.y;

  handleMesh.position.set(
    stampBoxFinal.max.x - handleBox.min.x + HANDLE_GAP_MM,
    stampBoxFinal.min.y - handleBox.min.y,
    stampCenter.z - handleCenter.z
  );

  stampGroup.add(handleMesh);
}
